import React, { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  HomeIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  DocumentTextIcon,
  UsersIcon,
  CogIcon,
  CommandLineIcon,
} from "@heroicons/react/24/outline";

// Import our new components
import Sidebar, {
  DefaultBrand,
  SidebarDivider,
  SidebarUserProfile,
} from "./ui/Sidebar";
import { NavigationAccordion } from "./ui/Accordion";
import ScrollAwareHeader from "./ui/ScrollAwareHeader";
import LandworthLogo from "./ui/LandworthLogo";
import { trackNavigation } from "../utils/analytics";

/**
 * Enhanced Layout Component with integrated UI components
 *
 * Features:
 * - Responsive sidebar with accordion navigation
 * - Scroll-aware header with search and user profile
 * - Proper navigation state management
 * - Mobile-optimized experience
 */
function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Demo user data
  const currentUser = {
    name: "John Doe",
    email: "john.doe@example.com",
    avatar: null,
  };

  // Enhanced navigation structure with accordion support
  const mainNavigation = [
    {
      id: "dashboard",
      name: "Dashboard",
      href: "/",
      // icon: <HomeIcon className="h-5 w-5" />,
      icon: "🏠",
    },
    {
      id: "valuations",
      name: "Valuations",
      // icon: <CurrencyDollarIcon className="h-5 w-5" />,
      icon: "💷",
      badge: { count: 8, color: "purple" },
      children: [
        { name: "All Valuations", href: "/valuations" },
        { name: "Recent Valuations", href: "/valuations?status=recent" },
        { name: "Pending Review", href: "/valuations?status=pending" },
        { name: "Approved Valuations", href: "/valuations?status=approved" },
      ],
    },
    {
      id: "projects",
      name: "Projects",
      // icon: <BuildingOfficeIcon className="h-5 w-5" />,
      icon: "🏗️",
      badge: { count: 12, color: "blue" },
      children: [
        { name: "All Projects", href: "/projects" },
        { name: "Active Projects", href: "/projects?status=active" },
        { name: "Completed Projects", href: "/projects?status=completed" },
        { name: "On-Hold Projects", href: "/projects?status=on-hold" },
      ],
    },
    {
      id: "deals",
      name: "Deals",
      // icon: <UserGroupIcon className="h-5 w-5" />,
      icon: "🤝",
      badge: { count: 5, color: "green" },
      children: [
        { name: "All Deals", href: "/deals" },
        { name: "Active Deals", href: "/deals?status=active" },
        { name: "Pending Deals", href: "/deals?status=pending" },
        { name: "Closed Deals", href: "/deals?status=closed" },
      ],
    },
    {
      id: "automations",
      name: "Automations",
      // icon: <CommandLineIcon className="h-5 w-5" />,
      icon: "🤖",
      badge: { count: 2, color: "indigo" },
      children: [
        { name: "All Automations", href: "/automations" },
        { name: "Active", href: "/automations?status=active" },
        { name: "Archived", href: "/automations?status=archived" },
      ],
    },
  ];

  const reportsNavigation = [
    {
      id: "analytics",
      name: "Analytics",
      // icon: <ChartBarIcon className="h-5 w-5" />,
      icon: "📈",
      children: [
        { name: "Overview", href: "/reports/overview" },
        { name: "Performance", href: "/reports/performance" },
        { name: "Trends", href: "/reports/trends" },
      ],
    },
    {
      id: "documents",
      name: "Documents",
      // icon: <DocumentTextIcon className="h-5 w-5" />,
      icon: "📁",
      children: [
        { name: "Templates", href: "/documents/templates" },
        { name: "Generated Reports", href: "/documents/reports" },
        { name: "Shared Files", href: "/documents/shared" },
      ],
    },
  ];

  const managementNavigation = [
    {
      id: "users",
      name: "User Management",
      // icon: <UsersIcon className="h-5 w-5" />,
      icon: "👥",
      children: [
        { name: "All Users", href: "/users" },
        { name: "Roles & Permissions", href: "/users/roles" },
        { name: "Team Settings", href: "/users/teams" },
      ],
    },
    {
      id: "settings",
      name: "System Settings",
      // icon: <CogIcon className="h-5 w-5" />,
      icon: "📈",
      children: [
        { name: "General", href: "/settings/general" },
        { name: "Integrations", href: "/settings/integrations" },
        { name: "Security", href: "/settings/security" },
        { name: "Backup", href: "/settings/backup" },
      ],
    },
  ];

  // Navigation handlers
  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  const handleSearch = (searchTerm) => {
    console.log("Search for:", searchTerm);
    // Implement search logic here
    // Could navigate to search results page or show search modal
  };

  const handleNotificationClick = () => {
    console.log("Notifications clicked");
    // Implement notifications logic here
  };

  const handleProfileClick = () => {
    console.log("Profile clicked");
    // Navigate to profile page or show profile menu
  };

  const handleNavigationClick = (e, item) => {
    // Handle navigation item clicks
    console.log("Navigation clicked:", item);

    // If item has href, navigate to it
    if (item.href) {
      // Track navigation
      trackNavigation(location.pathname, item.href);

      navigate(item.href);
    }

    // Close sidebar on mobile after navigation
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Scroll-Aware Header */}
      <ScrollAwareHeader
        behavior="blur"
        searchPlaceholder="Search projects, deals, valuations..."
        onSidebarToggle={handleSidebarToggle}
        onSearch={handleSearch}
        onNotificationClick={handleNotificationClick}
        onProfileClick={handleProfileClick}
        user={currentUser}
        logo={(scrollState) => (
          <LandworthLogo isCompact={scrollState.isCompact} />
        )}
      />

      {/* Layout Container for Sidebar + Main Content */}
      <div className="flex">
        {/* Enhanced Sidebar with Accordion Navigation */}
        <Sidebar
          isOpen={sidebarOpen}
          onClose={handleSidebarClose}
          brand={<DefaultBrand title="RE Platform" version="v4.0 MVP" />}
          footer={
            <SidebarUserProfile
              user={currentUser}
              onProfileClick={handleProfileClick}
              onSignOut={() => console.log("Sign out clicked")}
            />
          }
        >
          {/* Main Navigation Section */}
          <div className="space-y-1 mb-4">
            <NavigationAccordion
              items={mainNavigation}
              currentPath={location.pathname}
              onItemClick={handleNavigationClick}
            />
          </div>

          {/* Reports Section */}
          <SidebarDivider label="Reports" />
          <div className="space-y-1 mb-4">
            <NavigationAccordion
              items={reportsNavigation}
              currentPath={location.pathname}
              onItemClick={handleNavigationClick}
            />
          </div>

          {/* Management Section */}
          <SidebarDivider label="Management" />
          <div className="space-y-1">
            <NavigationAccordion
              items={managementNavigation}
              currentPath={location.pathname}
              onItemClick={handleNavigationClick}
            />
          </div>
        </Sidebar>

        {/* Main Content Area */}
        <div className="flex-1">
          {/* Content with proper spacing for fixed header */}
          <main className="pt-16 min-h-screen">
            <div className="p-4 lg:p-6">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default Layout;
